import { CommonModule, DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { ReviewerService } from 'src/app/core/services/reviewer.service';
import { RebarAuthService } from 'src/app/core/rebarauth/rebar.auth.service';
import { MasterService } from 'src/app/core/services/master.service';
import { UserRoleService } from 'src/app/core/services/user-role.service';
import { DateFormatService } from 'src/app/core/services/date-format.service';
import { threadId } from 'worker_threads';

interface ReviewCycleTemplate {
  name: string;
  path: string;
}

type PhaseType = 'PLAN' | 'CONDUCT' | 'REPORT' | 'GL_PRE_PLAN' | 'GL_PRE_CONDUCT' | 'GL_PRE_REPORT' | 'GL_POST_CONDUCT' | 'GL_POST_REPORT' | 'ODR_PLAN' | 'ODR_CONDUCT' | 'ODR_REPORT' | 'SPR_PLAN' | 'SPR_CONDUCT' | 'SPR_REPORT';
const PHASE_CONFIG: any = {
  PLAN: {
    phaseName: 'Plan Review',
    requiredSteps: [1, 3, 7, 8, 9],
    activityNames: {
      1: 'Initial Communication send',
      2: 'Kick off meeting',
      3: 'Actual kick off date',
      4: 'Meeting Minutes of Kick of Meeting Sent',
      5: 'Request send for Required Document (received) (Section-1)',
      6: 'Risk Assessment & draft Engagement Letter (EL)',
      7: 'Actual Planning meeting date',
      8: 'Meeting Minutes Documented & Distributed (Planning Meeting)',
      9: 'EL is published',
      10: 'Requested Document Received from Delivery Team (Section 2)'
    }
  },

  CONDUCT: {
    phaseName: 'Conduct Review',
    requiredSteps: [1, 6, 9, 10],
    activityNames: {
      1: 'Actual Fieldwork Start Date',
      2: 'Review documentation and define the controls to be tested (offline)',
      3: 'Agree review plan on Opening Call',
      4: 'Perform Fieldwork and document result in the working papers',
      5: 'Discuss Status and observation within schedule interim Meeting',
      6: 'Actual Fieldwork End Date',
      7: 'Prepare issue summary (ISAD) and share with SDL & team lead',
      8: 'Pre-closing is held to agree on observations',
      9: 'Actual Pre-Closing call meeting',
      10: 'Distribute Minute of meeting of Pre-closing call'
    }
  },

  REPORT: {
    phaseName: 'Report the Review',
    requiredSteps: [3, 4, 7, 9],
    activityNames: {
      1: 'Draft report based on agreed ISAD',
      2: 'Review/update Report',
      3: 'Send draft report for Management Response (MR)',
      4: 'Management Response Agreed?',
      5: 'Closing call to agree on MRs (Optional)',
      6: 'Update & publish the report',
      7: 'Overall Report Review Rating',
      8: 'Revised target publication date',
      9: 'Actual publishing date'
    }
  },

  GL_PRE_PLAN: {
    phaseName: 'Plan Review - Pre Go-Live',
    requiredSteps: [2, 3, 4],
    activityNames: {
      1: 'Kick off meeting',
      2: 'Actual kick off date',
      3: 'Meeting Minutes of Kick of Meeting Sent',
      4: 'EL is published',
      5: 'Information Request Document'
    }
  },

  GL_PRE_CONDUCT: {
    phaseName: 'Conduct Review - Pre Go-Live',
    requiredSteps: [1, 2],
    activityNames: {
      1: 'Actual Pre Go-Live (Fieldwork Start Date)',
      2: 'Perform Fieldwork and Document Result in Working Papers',
      3: 'Discuss Status and Observation within Schedule Interim Meeting'
    }
  },

  GL_PRE_REPORT: {
    phaseName: 'Report the Review - Pre Go-Live',
    requiredSteps: [1, 3, 4],
    activityNames: {
      1: 'Interim Pre Go-Live Report',
      2: 'Review/Update Interim Pre Go-Live Report',
      3: 'Actual Interim Call (Pre Go-Live Fieldwork End Date)',
      4: 'Meeting Minutes Send and MR Required',
      5: 'Exceptional Approval (if applicable)'
    }
  },

  GL_POST_CONDUCT: {
    phaseName: 'Conduct Review - Post Go-Live',
    requiredSteps: [1, 3, 5, 6],
    activityNames: {
      1: 'Actual Post Go-Live testing start / post go live fieldwork start date',
      2: 'Post-Go-Live-Working Paper',
      3: 'Actual Post Go-Live (Fieldwork End Date)',
      4: 'Pre-Closing is held to agree on observations',
      5: 'Actual Pre-Closing call meeting',
      6: 'Distribute Minute of meeting of Pre-closing call'
    }
  },

  GL_POST_REPORT: {
    phaseName: 'Report the Review - Post Go-Live',
    requiredSteps: [3, 4, 7, 9],
    activityNames: {
      1: 'Draft report based on agreed ISAD',
      2: 'Review/update Report',
      3: 'Send draft report for Management Response (MR)',
      4: 'Management Response Agreed?',
      5: 'Closing call to agree on MRs (Optional)',
      6: 'Update & publish the report',
      7: 'Overall Report Review Rating',
      8: 'Revised target publication date',
      9: 'Actual publishing date'
    }
  },

  ODR_PLAN: {
    phaseName: 'Plan Review - On-Demand',
    requiredSteps: [1, 3, 7, 8, 9],
    activityNames: {
      1: 'Initial Communication send',
      2: 'Kick off meeting',
      3: 'Actual kick off date',
      4: 'Meeting Minutes of Kick of Meeting Sent',
      5: 'Request send for Required Document (received) (Section-1)',
      6: 'Risk Assessment & draft Engagement Letter (EL)',
      7: 'Actual Planning meeting date',
      8: 'Meeting Minutes Documented & Distributed (Planning Meeting)',
      9: 'EL is published',
      10: 'Requested Document Received from Delivery Team (Section 2)'
    }
  },

  ODR_CONDUCT: {
    phaseName: 'Conduct Review - On-Demand',
    requiredSteps: [1, 6, 9, 10],
    activityNames: {
      1: 'Actual Fieldwork Start Date',
      2: 'Review documentation and define the controls to be tested (offline)',
      3: 'Agree review plan on Opening Call',
      4: 'Perform Fieldwork and document result in the working papers',
      5: 'Discuss Status and observation within schedule interim Meeting',
      6: 'Actual Fieldwork End Date',
      7: 'Prepare issue summary (ISAD) and share with SDL & team lead',
      8: 'Pre-closing is held to agree on observations',
      9: 'Actual Pre-Closing call meeting',
      10: 'Distribute Minute of meeting of Pre-closing call'
    }
  },

  ODR_REPORT: {
    phaseName: 'Report the Review - On-Demand',
    requiredSteps: [3, 4, 7, 9],
    activityNames: {
      1: 'Draft report based on agreed ISAD',
      2: 'Review/update Report',
      3: 'Send draft report for Management Response (MR)',
      4: 'Management Response Agreed?',
      5: 'Closing call to agree on MRs (Optional)',
      6: 'Update & publish the report',
      7: 'Overall Report Review Rating',
      8: 'Revised target publication date',
      9: 'Actual publishing date'
    }
  },

  SPR_PLAN: {
    phaseName: 'Plan Review - Special Project',
    requiredSteps: [1, 3, 7, 8, 9],
    activityNames: {
      1: 'Initial Communication send',
      2: 'Kick off meeting',
      3: 'Actual kick off date',
      4: 'Meeting Minutes of Kick of Meeting Sent',
      5: 'Request send for Required Document (received) (Section-1)',
      6: 'Risk Assessment & draft Engagement Letter (EL)',
      7: 'Actual Planning meeting date',
      8: 'Meeting Minutes Documented & Distributed (Planning Meeting)',
      9: 'EL is published',
      10: 'Requested Document Received from Delivery Team (Section 2)'
    }
  },

  SPR_CONDUCT: {
    phaseName: 'Conduct Review - Special Project',
    requiredSteps: [1, 6, 9, 10],
    activityNames: {
      1: 'Actual Fieldwork Start Date',
      2: 'Review documentation and define the controls to be tested (offline)',
      3: 'Agree review plan on Opening Call',
      4: 'Perform Fieldwork and document result in the working papers',
      5: 'Discuss Status and observation within schedule interim Meeting',
      6: 'Actual Fieldwork End Date',
      7: 'Prepare issue summary (ISAD) and share with SDL & team lead',
      8: 'Pre-closing is held to agree on observations',
      9: 'Actual Pre-Closing call meeting',
      10: 'Distribute Minute of meeting of Pre-closing call'
    }
  },

  SPR_REPORT: {
    phaseName: 'Report the Review - Special Project',
    requiredSteps: [3, 4, 7, 9],
    activityNames: {
      1: 'Draft report based on agreed ISAD',
      2: 'Review/update Report',
      3: 'Send draft report for Management Response (MR)',
      4: 'Management Response Agreed?',
      5: 'Closing call to agree on MRs (Optional)',
      6: 'Update & publish the report',
      7: 'Overall Report Review Rating',
      8: 'Revised target publication date',
      9: 'Actual publishing date'
    }
  },
};

@Component({
  selector: 'app-review-cycle',
  standalone: true,
  imports: [FormsModule, MatIconModule, CommonModule, MatTooltipModule],
  templateUrl: './review-cycle.component.html',
  styleUrl: './review-cycle.component.css',
  providers: [DatePipe]
})

export class ReviewCycleComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private masterService   = inject(MasterService);
  private userRoleService = inject(UserRoleService);

  get isRCMember(): boolean {
    return this.userRoleService.isRCMember;
  }
  @Input() reviewId!: string;
  @Input() reviewName!: string;
  @Input() reviewTypeId: number;   // 1 - STD; 2 - GLRR; 3 - ODR; 4 - SPR
  @Input() typeOfReview: string;
  @Input() isCancelled: boolean = false;
  @Input() isReadOnly: boolean = false;
  @Input() rcManager: string = '';
  @Output() reviewStatusChange = new EventEmitter<string>();
  @Output() toastNotification = new EventEmitter<string>();
  reviewIdNum!: number;
  kickoffDate = '';
  actualKickoffDate = '';
  actualPlanningDate = '';

  // Conduct Review
  actualFieldWorkStartDate = '';
  actualFieldWorkEndDate = '';
  preClosingCall = '';
  step2TextValue: string = '';
  step3ToggleValue: boolean = false;

  // Report Review
  reportRating: string = '';
  step5TextValue: string = '';
  reportReview = {
    reviewedByManager: '',
    reviewedByCompliance: ''
  };
  sendDraftReportforMR = '';
  MRAgreed = '';
  actualPublishingDate = '';

  // Revised target publication date (Report - Activity 8)
  showRevisedPubDateModal = false;
  revisedTargetPublicationDate = '';
  revisedPublicationDate = '';
  revisedDateReason = '';
  revisedDateAcknowledged = false;

  currentStep = 1;
  completedStepsMap: any = {
    PLAN: [], CONDUCT: [], REPORT: [], GL_PRE_PLAN: [], GL_PRE_CONDUCT: [], GL_PRE_REPORT: [], GL_POST_CONDUCT: [], GL_POST_REPORT: [], ODR_PLAN: [], ODR_CONDUCT: [], ODR_REPORT: [], SPR_PLAN: [], SPR_CONDUCT: [], SPR_REPORT: []
  };
  uploadedFilesMap: any = {
    PLAN: {}, CONDUCT: {}, REPORT: {}, GL_PRE_PLAN: {}, GL_PRE_CONDUCT: {}, GL_PRE_REPORT: {}, GL_POST_CONDUCT: {}, GL_POST_REPORT: {}, ODR_PLAN: {}, ODR_CONDUCT: {}, ODR_REPORT: {}, SPR_PLAN: {}, SPR_CONDUCT: {}, SPR_REPORT: {}
  };
  showNotApplicableModal = false;
  notApplicableReviewId: number;
  notApplicableReason = '';
  notApplicableFiles: any[] = [];
  showNotApplicableValidation = false;
  readonly maxFileSize = 20 * 1024 * 1024;
  readonly allowedExtensions = [
    'pdf', 'msg', 'xls', 'xlsx', 'doc', 'docx',
    'txt', 'ppt', 'pptx', 'jpg', 'jpeg', 'png', 'zip',
  ];
  showWarningModal = false;
  warningMessage = '';
  showSequenceWarningModal = false;
  private _scrollLockCount = 0;
  private _scrollLockY = 0;
  private _activePicker: HTMLInputElement | null = null;
  private _pickerResizeHandler: (() => void) | null = null;
  templatesModalOpen = false;
  showUploadDocModal = false;
  pendingUploadStep: number | null = null;
  pendingUploadPhase: PhaseType | null = null;
  isDownloadingAll = false;
  isLoading = false;
  userName: string;
  activityDateMap: any = {};
  showRevisedDateValidation = false;
  //IsNotApplicable
  isPlanReviewReadOnly = false;

  private dateFormatSvc = inject(DateFormatService);

  // Go-Live
  glActiveTab: 'pre' | 'post' = 'pre';
  glPrePlanKickoffDate = '';
  glPrePlanActualKickoffDate = '';
  // Pre Go-Live Conduct
  glPreConductFieldworkStartDate = '';
  // Pre Go-Live Report
  glPreReportInterimCallDate = '';
  glPreReportReview = { reviewedByManager: '', reviewedByCompliance: '' };
  // Post Go-Live Conduct
  glPostConductFieldworkStartDate = '';
  glPostConductFieldworkEndDate = '';
  glPostConductPreClosingCall = '';
  // Report Review - Post Go-Live
  step5glTextValue: string = 'Not Started';
  glreportRating: string = '';
  glreportReview = {
    glreviewedByManager: '',
    glreviewedByCompliance: ''
  };
  glsendDraftReportforMR = '';
  glMRAgreed = '';
  glactualPublishingDate = '';

  constructor(private reviewerService: ReviewerService, private rebarAuth: RebarAuthService, private datePipe: DatePipe) { }

  formatDateDisplay(value: string): string {
    return this.dateFormatSvc.format(value);
  }

  // Opens the date picker for the given input element, step, and phase.
  // If the step and phase are blocked by sequence, the date picker will not open.
  openDatePicker(input: HTMLInputElement, step?: number, phase?: PhaseType): void {
    if (step != null && phase != null && this.isBlockedBySequence(step, phase)) return;
    if (input.disabled) return;
    this._activePicker = input;
    input?.showPicker?.();
  }

  ngOnInit() {
    this.isLoading = true;
    this.reviewIdNum = +this.reviewId;
    this.notApplicableReviewId = +this.reviewId;
    this.userName = this.rebarAuth.getUserName();
    if (this.typeOfReview === 'post-only') {
      this.glActiveTab = 'post';
    }

    this._pickerResizeHandler = () => {
      if (this._activePicker && !this._activePicker.disabled) {
        const picker = this._activePicker;
        picker.blur();
        setTimeout(() => picker.showPicker?.(), 0);
      }
    };
    window.addEventListener('resize', this._pickerResizeHandler);
    this.destroyRef.onDestroy(() => {
      if (this._pickerResizeHandler) {
        window.removeEventListener('resize', this._pickerResizeHandler);
      }
    });

    this.reviewerService.getKickOffDatebyID(this.reviewIdNum).subscribe(res => {
      this.kickoffDate = this.datePipe.transform(res[0].KickOffCallTargetDate,'dd-MMM-yyyy') ?? '';
    });

     this.reviewerService.getKickOffDatebyID(this.reviewIdNum).subscribe(res => {
      this.glPrePlanKickoffDate = this.datePipe.transform(res[0].KickOffCallTargetDate,'dd-MMM-yyyy') ?? '';
    });

    this.reviewerService.getReviewActivities(this.reviewIdNum)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          if (res?.Activities?.length) {
            const plan = res.Activities.filter(a => a.Phase === 'Plan Review');
            const conduct = res.Activities.filter(a => a.Phase === 'Conduct Review');
            const report = res.Activities.filter(a => a.Phase === 'Report the Review');

            this.isPlanReviewReadOnly = res.Activities?.some((x: any) => x.IsNotApplicable === 'true') || false;

            // this.bindActivities(plan, 'PLAN');
            // this.bindActivities(conduct, 'CONDUCT');
            // this.bindActivities(report, 'REPORT');

            if (this.reviewTypeId === 1) {
              this.bindActivities(plan, 'PLAN');
              this.bindActivities(conduct, 'CONDUCT');
              this.bindActivities(report, 'REPORT');
            }
            else if (this.reviewTypeId === 3 || this.reviewTypeId === 4) {

              const planPhase = this.getPhaseName('PLAN');
              const conductPhase = this.getPhaseName('CONDUCT');
              const reportPhase = this.getPhaseName('REPORT');

              const planActs = res.Activities.filter(
                (a: any) => a.Phase === PHASE_CONFIG[planPhase].phaseName
              );

              const conductActs = res.Activities.filter(
                (a: any) => a.Phase === PHASE_CONFIG[conductPhase].phaseName
              );

              const reportActs = res.Activities.filter(
                (a: any) => a.Phase === PHASE_CONFIG[reportPhase].phaseName
              );

              this.bindActivities(planActs, planPhase);
              this.bindActivities(conductActs, conductPhase);
              this.bindActivities(reportActs, reportPhase);
            }

            if (this.reviewTypeId === 2) {
              const glPrePlan = res.Activities.filter((a: any) => a.Phase === 'Plan Review - Pre Go-Live');
              const glPreConduct = res.Activities.filter((a: any) => a.Phase === 'Conduct Review - Pre Go-Live');
              const glPreReport = res.Activities.filter((a: any) => a.Phase === 'Report the Review - Pre Go-Live');
              const glPostConduct = res.Activities.filter((a: any) => a.Phase === 'Conduct Review - Post Go-Live');
              const glPostReport = res.Activities.filter((a: any) => a.Phase === 'Report the Review - Post Go-Live');
              this.bindActivities(glPrePlan, 'GL_PRE_PLAN');
              this.bindActivities(glPreConduct, 'GL_PRE_CONDUCT');
              this.bindActivities(glPreReport, 'GL_PRE_REPORT');
              this.bindActivities(glPostConduct, 'GL_POST_CONDUCT');
              this.bindActivities(glPostReport, 'GL_POST_REPORT');
            }
          }
          this.isLoading = false;
        },
        error: err => {
          console.error('getReviewActivities error', err);
          this.isLoading = false;
          this.openWarning('Unable to load review cycle activities. Please try again.');
        },
      });
  }

  getPhaseName(basePhase: 'PLAN' | 'CONDUCT' | 'REPORT'): PhaseType {

    if (this.reviewTypeId === 3) {
      return `ODR_${basePhase}` as PhaseType;
    }
    if (this.reviewTypeId === 4) {
      return `SPR_${basePhase}` as PhaseType;
    }
    return basePhase as PhaseType;
  }

  reviewCycleTemplates: ReviewCycleTemplate[] = [
    { name: '1.1.B.1.Risk_and_Compliance_Review_KickOff_Meeting_InviteeslistReq_TEMPLATE_v1.4_Jul2025', path: 'assets/documents/sample 1.docx' },
    { name: '1.1.B.1.Risk and Compliance_Review_KickOff_Mtg_Invite_TEMPLATE_v4.0', path: 'assets/documents/sample 2.docx' },
    { name: 'Risk and Compliance_Review_KickOff_deck_TEMPLATE_v1.1', path: 'assets/documents/sample 3.docx' },
    { name: 'RC Information Request for Contract Management v3.1', path: 'assets/documents/sample 4.docx' },
    { name: 'RC Information Request for Risk Assessment_Phase 2_Updated v2', path: 'assets/documents/sample 5.docx' },
    { name: 'Risk and Compliance_Executive_Summary_Process Approach v10 IRM', path: 'assets/documents/sample 6.docx' },
    { name: '1.1.B.2.Risk and Compliance Review KickOff Meeting Minutes TEMPLATE v2.6', path: 'assets/documents/sample 7.docx' },
  ];

  private _lockScroll(): void {
    if (this._scrollLockCount === 0) {
      this._scrollLockY = window.scrollY;
      document.body.style.top = `-${this._scrollLockY}px`;
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    }
    this._scrollLockCount++;
  }

  private _unlockScroll(): void {
    if (this._scrollLockCount <= 0) return;
    this._scrollLockCount--;
    if (this._scrollLockCount === 0) {
      document.body.style.top = '';
      document.body.style.position = '';
      document.body.style.width = '';
      window.scrollTo(0, this._scrollLockY);
    }
  }

  openWarning(message: string) {
    this.warningMessage = message;
    this.showWarningModal = true;
    this._lockScroll();
  }

  hasInvalidExtension(files: FileList): boolean {
    return Array.from(files).some(f => {
      const ext = f.name.split('.').pop()?.toLowerCase() ?? '';
      return !this.allowedExtensions.includes(ext);
    });
  }

  closeWarning() {
    this.showWarningModal = false;
    this.warningMessage = '';
    this._unlockScroll();
  }

  openSequenceWarning() {
    this.showSequenceWarningModal = true;
    this._lockScroll();
  }

  closeSequenceWarning() {
    this.showSequenceWarningModal = false;
    this._unlockScroll();
  }


  // -- Sequence Warning Handling --
  isBlockedBySequence(step: number, phase: PhaseType): boolean {
    if (this.isPlanReviewReadOnly) return false;
    const isMandatory = PHASE_CONFIG[phase]?.requiredSteps?.includes(step);
    if (!isMandatory) return false;

    // If the step is not active and not completed, it is blocked by sequence rules.
    if (this.isActive(step, phase) || this.isCompleted(step, phase)) return false;
    this.openSequenceWarning();
    return true;
  }

  openNotApplicableModal() {
    this.showNotApplicableModal = true;
    this._lockScroll();
  }

  closeNotApplicableModal() {
    this.showNotApplicableModal = false;
    this.notApplicableReason = '';
    this.notApplicableFiles = [];
    this.showNotApplicableValidation = false;
    this._unlockScroll();
  }

  get isNotApplicableFormDirty(): boolean {
    return this.notApplicableReason.trim() !== '' || this.notApplicableFiles.length > 0;
  }

  removeNotApplicableFile(index: number) {
    this.notApplicableFiles.splice(index, 1);
  }

  confirmNotApplicable() {
    if (!this.notApplicableReason.trim()) {
      this.showNotApplicableValidation = true;
      return;
    }
    this.showNotApplicableValidation = false;
    this.showNotApplicableModal = false;
    this._unlockScroll();
    const planPhase = this.getPhaseName('PLAN');
    this.insertNewRequest(planPhase, { files: [...this.notApplicableFiles] });
    this.toastNotification.emit('The Request for New Not Applicable Request has been sent successfully.');
  }

  openRevisedPubDateModal() {
    //if (!this.isActive(8, 'REPORT') && !this.isCompleted(8, 'REPORT')) return;
    const reportPhase = this.getPhaseName('REPORT');
   if (!(this.isActive(8, reportPhase) || this.isCompleted(8, reportPhase)) &&
          !(this.isActive(8, 'GL_POST_REPORT') || this.isCompleted(8, 'GL_POST_REPORT'))) {
      return; }
    this.revisedPublicationDate = this.revisedTargetPublicationDate || '';
    this.showRevisedDateValidation = false;
    this.showRevisedPubDateModal = true;
    this._lockScroll();
  }

  /** Latest selectable revised publication date (today + 180 days) as yyyy-MM-dd. */
  get revisedPubDateMax(): string {
    const d = new Date();
    d.setDate(d.getDate() + 180);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  isRevisedPubDateBeyondLimit(): boolean {
    return !!this.revisedPublicationDate && this.revisedPublicationDate.substring(0, 10) > this.revisedPubDateMax;
  }

  closeRevisedPubDateModal() {
    this.showRevisedPubDateModal = false;
    this.revisedPublicationDate = this.revisedTargetPublicationDate || '';
    this.revisedDateReason = '';
    this.revisedDateAcknowledged = false;
    this.showRevisedDateValidation = false;
    this._unlockScroll();
  }

  submitRevisedPubDate(phase: PhaseType) {
    let phasename: any;
    if(phase === 'REPORT') { phasename = 'REPORT';}
    if(phase === 'ODR_REPORT') { phasename = 'ODR_REPORT';}
    if(phase === 'SPR_REPORT') { phasename = 'SPR_REPORT';}
    if(phase === 'GL_POST_REPORT') { phasename = 'GL_POST_REPORT';}
    if (!this.revisedPublicationDate || !this.revisedDateReason?.trim() || !this.revisedDateAcknowledged || this.isRevisedPubDateBeyondLimit()) {
      this.showRevisedDateValidation = true;
      return;
    }
    this.showRevisedDateValidation = false;
    this.saveActivity(8, phasename, {
      date: this.revisedPublicationDate,
      text: this.revisedDateReason,
      afterSave: () => {
        this.revisedTargetPublicationDate = this.revisedPublicationDate;
        this.showRevisedPubDateModal = false;
        this.revisedDateReason = '';
        this.revisedDateAcknowledged = false;
        this._unlockScroll();
        this.toastNotification.emit('The Request for Revised Target Publication date has been sent successfully');
      }
    });
    this.insertNewRequest(phasename, { date: this.revisedPublicationDate });
  }

  onDateSelected(step: number, phase: PhaseType) {
    let value: any;

    if(phase === 'PLAN' || phase === 'ODR_PLAN' || phase === 'SPR_PLAN') {
      if (step === 2) value = this.kickoffDate;
      if (step === 3) value = this.actualKickoffDate;
      if (step === 7) value = this.actualPlanningDate; 
    }
    if(phase === 'CONDUCT' || phase === 'ODR_CONDUCT' || phase === 'SPR_CONDUCT') {
      if (step === 1) value = this.actualFieldWorkStartDate;
      if (step === 6) value = this.actualFieldWorkEndDate;
      if (step === 9) value = this.preClosingCall; 
    }
    if (phase == 'REPORT' || phase === 'ODR_REPORT' || phase === 'SPR_REPORT') {
      if (step === 3) value = this.sendDraftReportforMR;
      if (step === 4) value = this.MRAgreed;
      if (step === 9) value = this.actualPublishingDate;
    }
    if (phase === 'GL_PRE_PLAN') {
      if (step === 2) value = this.glPrePlanActualKickoffDate;
    }
    if (phase === 'GL_PRE_CONDUCT') {
      if (step === 1) value = this.glPreConductFieldworkStartDate;
    }
    if (phase === 'GL_PRE_REPORT') {
      if (step === 3) value = this.glPreReportInterimCallDate;
    }
    if (phase === 'GL_POST_CONDUCT') {
      if (step === 1) value = this.glPostConductFieldworkStartDate;
      if (step === 3) value = this.glPostConductFieldworkEndDate;
      if (step === 5) value = this.glPostConductPreClosingCall;
    }
     if (phase == 'GL_POST_REPORT') {
      if (step === 3) value = this.glsendDraftReportforMR;
      if (step === 4) value = this.glMRAgreed;
      if (step === 9) value = this.glactualPublishingDate;
    }

    // Block clearing a mandatory field's previously saved date via the native picker's Clear button.
    if (!value) {
      if (this.isMandatoryDateField(step, phase) && this.activityDateMap[`${phase}_${step}`]) {
        this.openWarning('This date cannot be cleared. Please select a new date from the calendar.');
        this.clearDate(step, phase);
      }
      return;
    }

    const selected = new Date(value);
    selected.setHours(0, 0, 0, 0);

    if (step === 1 && this.actualFieldWorkEndDate && value) {
      const end = new Date(this.actualFieldWorkEndDate);
      end.setHours(0, 0, 0, 0);
      if (selected.getTime() > end.getTime()) {
        this.openWarning('Actual Fieldwork Start Date cannot be later than the Actual Fieldwork End Date. Please select a valid date.');
        this.clearDate(step, phase);
        return;
      }
    }
    if (step === 6 && this.actualFieldWorkStartDate && value) {
      const start = new Date(this.actualFieldWorkStartDate);
      start.setHours(0, 0, 0, 0);

      if (selected.getTime() < start.getTime()) {
        this.openWarning('Actual Fieldwork End Date cannot be earlier than the Actual Fieldwork Start Date. Please select a valid date.');
        this.clearDate(step, phase);
        return;
      }
    }
    if (phase === 'GL_POST_CONDUCT' && step === 1 && this.glPostConductFieldworkEndDate) {
      const end = new Date(this.glPostConductFieldworkEndDate);
      end.setHours(0, 0, 0, 0);
      if (selected.getTime() > end.getTime()) {
        this.openWarning('Actual Fieldwork Start Date cannot be later than the Actual Fieldwork End Date. Please select a valid date.');
        this.clearDate(step, phase);
        return;
      }
    }
    if (phase === 'GL_POST_CONDUCT' && step === 3 && this.glPostConductFieldworkStartDate) {
      const start = new Date(this.glPostConductFieldworkStartDate);
      start.setHours(0, 0, 0, 0);
      if (selected.getTime() < start.getTime()) {
        this.openWarning('Actual Fieldwork End Date cannot be earlier than the Actual Fieldwork Start Date. Please select a valid date.');
        this.clearDate(step, phase);
        return;
      }
    }
    this.saveActivity(step, phase, { date: value }); //API Call
  }

  // Derives mandatory status from PHASE_CONFIG.requiredSteps
  private isMandatoryDateField(step: number, phase: PhaseType): boolean {
    return !!PHASE_CONFIG[phase]?.requiredSteps?.includes(step);
  }

  clearDate(step: number, phase: PhaseType) {
    const oldValue = this.activityDateMap[`${phase}_${step}`] || null;
    setTimeout(() => {

      if (step === 2) {
        this.kickoffDate = oldValue;
      } else if (step === 3) {
        if (phase === 'PLAN' || phase === 'ODR_PLAN' || phase === 'SPR_PLAN') {
          this.actualKickoffDate = oldValue;
        } else if (

          phase === 'REPORT' ||

          phase === 'ODR_REPORT' ||

          phase === 'SPR_REPORT'

        ) {
          this.sendDraftReportforMR = oldValue;
        } else if (phase === 'GL_POST_REPORT') {
          this.glsendDraftReportforMR = oldValue;
        }
      } else if (step === 4 && (

        phase === 'REPORT' ||

        phase === 'ODR_REPORT' ||

        phase === 'SPR_REPORT'

      )
      ) {
        this.MRAgreed = oldValue;
      } else if (step === 4 && phase === 'GL_POST_REPORT') {
        this.glMRAgreed = oldValue;
      }
      else if (step === 7) {
        this.actualPlanningDate = oldValue;
      } else if (step === 1) {
        this.actualFieldWorkStartDate = oldValue;
      } else if (step === 6) {
        this.actualFieldWorkEndDate = oldValue;
      } else if (step === 9) {
        if (

          phase === 'CONDUCT' ||

          phase === 'ODR_CONDUCT' ||

          phase === 'SPR_CONDUCT'

        ) {
          this.preClosingCall = oldValue;
        } else if (

          phase === 'REPORT' ||

          phase === 'ODR_REPORT' ||

          phase === 'SPR_REPORT'

        ) {
          this.actualPublishingDate = oldValue;
        } else if (phase === 'GL_POST_REPORT') {
          this.glactualPublishingDate = oldValue;
        }
      }
      if (phase === 'GL_PRE_PLAN') {
        if (step === 2) this.glPrePlanActualKickoffDate = oldValue;
      }
      if (phase === 'GL_PRE_CONDUCT') {
        if (step === 1) this.glPreConductFieldworkStartDate = oldValue;
      }
      if (phase === 'GL_PRE_REPORT') {
        if (step === 3) this.glPreReportInterimCallDate = oldValue;
      }
      if (phase === 'GL_POST_CONDUCT') {
        if (step === 1) this.glPostConductFieldworkStartDate = oldValue;
        if (step === 3) this.glPostConductFieldworkEndDate = oldValue;
        if (step === 5) this.glPostConductPreClosingCall = oldValue;
      }
    });
  }

  completeStep(step: number, phase: PhaseType) {
    if (this.isCompleted(step, phase)) return;
    // Prevent cross-phase pollution
    if (!this.completedStepsMap[phase]) {
      this.completedStepsMap[phase] = [];
    }

    this.completedStepsMap[phase] = [
      ...this.completedStepsMap[phase],
      step
    ];
  }

  isCompleted(step: number, phase: PhaseType) {
    // if (step === 2 && phase === 'PLAN') {
    //   const required = PHASE_CONFIG['PLAN'].requiredSteps;
    //   return required.every((s: number) => this.completedStepsMap['PLAN'].includes(s));
    // }
    // return this.completedStepsMap[phase].includes(step);
    if (

      step === 2 &&

      (phase === 'PLAN' ||

        phase === 'ODR_PLAN' ||
        phase === 'SPR_PLAN')

    ) {

      const required = PHASE_CONFIG[phase].requiredSteps;



      return required.every((s: number) =>

        this.completedStepsMap[phase].includes(s)

      );

    }
    if (!this.completedStepsMap[phase].includes(step)) return false;
    const files = this.uploadedFilesMap[phase]?.[step];
    if (Array.isArray(files) && files.length === 0) return false;
    if (step === 3 && (phase === 'CONDUCT' || phase === 'ODR_CONDUCT' || phase === 'SPR_CONDUCT') && !this.step3ToggleValue) return false;
    return true;
  }

  isActive(step: number, phase: PhaseType) {

    if ((phase === 'CONDUCT' || phase === 'ODR_CONDUCT' || phase === 'SPR_CONDUCT') && step === 1) {
      return this.isConductUnlocked();
    }

    if ((phase === 'REPORT' || phase === 'ODR_REPORT' || phase === 'SPR_REPORT') && step === 3) {
      return this.isReportUnlocked();
    }

    if (phase === 'GL_PRE_CONDUCT' && step === 1) {
      return this.isGLConductUnlocked();
    }

    if (phase === 'GL_PRE_REPORT' && step === 1) {
      return this.isGLReportUnlocked();
    }

    if (phase === 'GL_POST_CONDUCT' && step === 1) {
      return this.isGLPostConductUnlocked();
    }

    if (phase === 'GL_POST_REPORT' && step === 3) {
      return this.isGLPostReportUnlocked();
    }
  
    const requiredSteps = PHASE_CONFIG[phase].requiredSteps;
    if (!requiredSteps.includes(step)) return true;

    const currentStep = requiredSteps.find(s => !this.completedStepsMap[phase].includes(s));
    return step === currentStep;
  }

  moveToNextRequiredStep(phase: PhaseType) {
    const requiredSteps = PHASE_CONFIG[phase].requiredSteps;
    const remaining = requiredSteps.filter(s => !this.completedStepsMap[phase].includes(s));

    return remaining.length ? remaining[0] : null;
  }

  onFileSelected(event: any, step: number, phase: PhaseType) {
    const input = event.target;
    const files = input.files;
    if (!files || files.length === 0) return;

    if (this.hasInvalidExtension(files)) {
      input.value = null;
      this.openWarning('Invalid file. Please upload a supported file type (PDF, MSG, XLS, XLSX, DOC, DOCX, TXT, PPT, PPTX, JPG, JPEG, PNG, ZIP) and ensure the file size does not exceed 20 MB.');
      return;
    }

    const emptyFiles = Array.from(files as FileList).filter(f => f.size === 0);
    if (emptyFiles.length > 0) {
      input.value = null;
      this.openWarning('Invalid file. Please upload a supported file type (PDF, MSG, XLS, XLSX, DOC, DOCX, TXT, PPT, PPTX, JPG, JPEG, PNG, ZIP) and ensure the file size does not exceed 20 MB.');
      return;
    }

    const oversized = Array.from(files as FileList).filter(f => f.size > this.maxFileSize);
    if (oversized.length > 0) {
      input.value = null;
      this.openWarning('Invalid file. Please upload a supported file type (PDF, MSG, XLS, XLSX, DOC, DOCX, TXT, PPT, PPTX, JPG, JPEG, PNG, ZIP) and ensure the file size does not exceed 20 MB.');
      return;
    }

    this.saveActivity(step, phase, { files: Array.from(files) }); //API Call
    input.value = null;
  }

  removeFile(step: number, index: number, phase: PhaseType) {
    const file = this.uploadedFilesMap[phase][step][index];
    const isMandatory = PHASE_CONFIG[phase]?.requiredSteps?.includes(step);
    // Only block last-file deletion for mandatory activities; optional ones can revert to uncompleted.
    if (isMandatory && this.uploadedFilesMap[phase][step]?.length <= 1) {
      this.openWarning('You must upload a replacement document before deleting the only existing file.');
      return;
    }

    const payload = {
      params: {
        category: 'DeleteReviewCycleDocument',
        ReviewID: this.reviewIdNum,
        PhaseName: PHASE_CONFIG[phase].phaseName,
        ActivityNumber: step,
        DocumentName: file.name
      }
    };

    // Added loading indicator for the delete operation
    this.isLoading = true;
    this.reviewerService.deleteReviewCycleDocument(payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: () => {
          this.uploadedFilesMap[phase][step].splice(index, 1);
          if (this.uploadedFilesMap[phase][step].length === 0) {
            this.completedStepsMap[phase] = this.completedStepsMap[phase].filter((s: number) => s !== step);
          }
        },
        error: err => {
          console.error('deleteReviewCycleDocument error', err);
          this.openWarning('Unable to delete the document. Please try again.');
        },
      });
  }

  getStatus(phase: PhaseType) {
    const completed = this.completedStepsMap[phase] || [];
    const required = PHASE_CONFIG[phase]?.requiredSteps || [];

    const completedMandatory = completed.filter(step => required.includes(step));

    if (completedMandatory.length === 0) {
      return 'NOT_STARTED';
    }
    const allMandatoryCompleted = completedMandatory.length === required.length;
    return allMandatoryCompleted ? 'COMPLETED' : 'IN_PROGRESS';
  }

  openTemplatesModal(): void {
    this.templatesModalOpen = true;
    this._lockScroll();
  }

  closeTemplatesModal(): void {
    this.templatesModalOpen = false;
    this._unlockScroll();
  }

  downloadTemplate(template: ReviewCycleTemplate): void {
    const link = document.createElement('a');
    link.href = encodeURI(template.path);
    link.download = template.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  async downloadAllTemplates(): Promise<void> {
    if (this.isDownloadingAll || this.reviewCycleTemplates.length === 0) return;
    this.isDownloadingAll = true;
    try {
      const zip = new JSZip();
      await Promise.all(
        this.reviewCycleTemplates.map(async (template) => {
          const response = await fetch(encodeURI(template.path));
          if (!response.ok) {
            throw new Error(`Failed to fetch ${template.name} (${response.status})`);
          }
          zip.file(template.name, await response.blob());
        })
      );
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, 'Review_Cycle_Templates.zip');
    } catch (err) {
      console.error('Download All templates failed:', err);
    } finally {
      this.isDownloadingAll = false;
    }
  }

  // API call
  async saveActivity(step: number, phase: PhaseType, options?: { date?: string, files?: File[], text?: string, toggle?: boolean, activityName?: string, afterSave?: () => void }) {
    this.isLoading = true;
    const config = PHASE_CONFIG[phase];
    const payload = {
      ReviewID: this.reviewIdNum,
      CreatedBy: this.userName,
      ReviewStatus: this.getReviewStatusByStep(step, phase),
      Activity: {
        Phase: config.phaseName,
        ActivityNumber: step,
        ActivityName: options?.activityName || config.activityNames[step],
        IsMandatory: config.requiredSteps.includes(step),
        IsCompleted: true,
        IsNotApplicable: false,
        NotApplicableReason: null,
        DateValue: options?.date || null,
        ToggleValue: options?.toggle ?? false,
        TextValue: options?.text || null,
        ActivityStatus: this.getActivityStatus(phase, step),
        IsActive: true
      },
      Documents: []
    };

    // Attach documents if present
    if (options?.files?.length) {

      const docs = await Promise.all(
        options.files.map(async (f) => {
          const base64 = await this.readFileasBase64(f);

          return {
            FileName: f.name,
            StorageFilePath: `${this.reviewIdNum}/${this.getPhaseFolder(phase)}/Activity_${step}/${f.name}`,
            FileSize: f.size,
            FileType: f.name.split('.').pop(),
            fileContent: base64,
            UploadedBy: this.userName
          };
        })
      );
      payload.Documents = docs;
    }
    // API Call
    this.reviewerService.saveReviewCycleActivity(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          console.log('saveReviewCycleActivity response:', res);
          // Mark the step as complete and update the activity date and uploaded files maps.
          this.completeStep(step, phase);
          if (options?.date) {
            this.activityDateMap[`${phase}_${step}`] = options.date;
          }
          // Update the uploaded files map if there are any files provided after the save operation.
          if (options?.files?.length) {
            if (!this.uploadedFilesMap[phase][step]) {
              this.uploadedFilesMap[phase][step] = [];
            }
            const mapped = options.files.map(f => ({
              name: f.name,
              fileName: f.name,
              storagePath: `${this.reviewIdNum}/${this.getPhaseFolder(phase)}/Activity_${step}/${f.name}`,
              existing: true
            }));
            this.uploadedFilesMap[phase][step].push(...mapped);
          }
          //this.reviewStatusChange.emit(this.getLatestReviewStatus(phase));
          const reviewStatus = res?.ReviewStatus?.trim() || res?.[0]?.ReviewStatus?.trim();
          if (reviewStatus) {
            this.reviewStatusChange.emit(reviewStatus);
          }
          this.isLoading = false;
          options?.afterSave?.();
        },
        error: err => {
          console.error('saveReviewCycleActivity error', err);
          this.isLoading = false;
          this.openWarning('Unable to save the activity. Please try again.');
        },
      });
  }

  getPhaseFolder(phase: string): string {
    const map = {
      PLAN: 'PlanReview',
      CONDUCT: 'ConductReview',
      REPORT: 'ReportReview',
      GL_PRE_PLAN: 'PlanReviewPreGoLive',
      GL_PRE_CONDUCT: 'ConductReviewPreGoLive',
      GL_PRE_REPORT: 'ReportReviewPreGoLive',
      GL_POST_CONDUCT: 'ConductReviewPostGoLive',
      GL_POST_REPORT: 'ReportReviewPostGoLive',
      ODR_PLAN: 'PlanReviewOnDemand',
      ODR_CONDUCT: 'ConductReviewOnDemand',
      ODR_REPORT: 'ReportReviewOnDemand',
      SPR_PLAN: 'PlanReviewSpecialProject',
      SPR_CONDUCT: 'ConductReviewSpecialProject',
      SPR_REPORT: 'ReportReviewSpecialProject'
    };
    return map[phase] || phase;
  }

  readFileasBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;

        // remove "data:...base64," prefix
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  }

  getReviewStatusByStep(step: number, phase: PhaseType): string {
    const statusMap: any = {
      PLAN: {
        1: 'PL - Initial Communication Sent',
        3: 'PL - Kick Off Call Held',
        7: 'PL - FW Confirmed',
        8: 'SCP - Engagement Letter Issued',
        9: 'SCP - Engagement Letter Issued'
      },

      CONDUCT: {
        1: 'FW - Started',
        6: 'FW - Completed',
        9: 'RPTG - Pre Close Call Held',
        10: 'RPTG - Pre Close Call Held'
      },

      REPORT: {
        3: 'RPTG - Draft Sent for Mgmt Resp',
        4: 'RPTG - Mgmt Resp Agreed',
        7: 'RPTG - Mgmt Resp Agreed',
        9: 'IM - Published',
      },

      GL_PRE_PLAN: {
        2: 'PL - Kick Off Call Held',
        3: 'PL - FW Confirmed',
        4: 'SCP - Engagement Letter Issued'
      },

      GL_PRE_CONDUCT: {
        1: 'Pre-Go-Live FW - Started'
      },

      GL_PRE_REPORT: {
        1: 'Pre-Go-Live FW - Completed',
        3: 'Interim Call Held'
      },

      GL_POST_CONDUCT: {
        1: 'POST-Go-Live FW - Started',
        3: 'POST-Go-Live FW - Completed',
        5: 'RPTG - Pre Close Call Held',
      },

      GL_POST_REPORT: {
        3: 'RPTG - Draft Sent for Mgmt Resp',
        4: 'RPTG - Mgmt Resp Agreed',
        9: 'IM - Published - Issue Management'
      },

      ODR_PLAN: {
        1: 'PL - Initial Communication Sent',
        3: 'PL - Kick Off Call Held',
        7: 'PL - FW Confirmed',
        8: 'SCP - Engagement Letter Issued',
        9: 'SCP - Engagement Letter Issued'
      },

      ODR_CONDUCT: {
        1: 'FW - Started',
        6: 'FW - Completed',
        9: 'RPTG - Pre Close Call Held',
        10: 'RPTG - Pre Close Call Held'
      },

      ODR_REPORT: {
        3: 'RPTG - Draft Sent for Mgmt Resp',
        4: 'RPTG - Mgmt Resp Agreed',
        7: 'RPTG - Mgmt Resp Agreed',
        9: 'IM - Published',
      },

      SPR_PLAN: {
        1: 'PL - Initial Communication Sent',
        3: 'PL - Kick Off Call Held',
        7: 'PL - FW Confirmed',
        8: 'SCP - Engagement Letter Issued',
        9: 'SCP - Engagement Letter Issued'
      },

      SPR_CONDUCT: {
        1: 'FW - Started',
        6: 'FW - Completed',
        9: 'RPTG - Pre Close Call Held',
        10: 'RPTG - Pre Close Call Held'
      },

      SPR_REPORT: {
        3: 'RPTG - Draft Sent for Mgmt Resp',
        4: 'RPTG - Mgmt Resp Agreed',
        7: 'RPTG - Mgmt Resp Agreed',
        9: 'IM - Published',
      },
    };
    return statusMap[phase]?.[step] || null;
  }

  getActivityStatus(phase: PhaseType, pendingStep?: number): string {
    const required = PHASE_CONFIG[phase].requiredSteps;
    const completed = [...(this.completedStepsMap[phase] ?? [])];
    if (pendingStep !== undefined) completed.push(pendingStep);

    if (required.every(step => completed.includes(step))) {
      return "Completed";
    }
    return "In Progress";
  }

  bindActivities(activities: any[], phase: PhaseType) {
    this.completedStepsMap[phase] = [];
    activities.forEach(act => {
      const step = act.ActivityNumber;

      // Mark completed steps
      if (act.IsCompleted) {
        this.completedStepsMap[phase].push(step);
      }

      // Bind dates
      if (act.DateValue) {
        const date = new Date(act.DateValue);
        const formatted = date.toISOString().split('T')[0];
        this.activityDateMap[`${phase}_${step}`] = formatted;
        if (phase === 'PLAN' || phase === 'ODR_PLAN' || phase === 'SPR_PLAN') {
          if (step === 2) this.kickoffDate = formatted;
          if (step === 3) this.actualKickoffDate = formatted;
          if (step === 7) this.actualPlanningDate = formatted;
        }

        if (phase === 'CONDUCT' || phase === 'ODR_CONDUCT' || phase === 'SPR_CONDUCT') {
          if (step === 1) this.actualFieldWorkStartDate = formatted;
          if (step === 6) this.actualFieldWorkEndDate = formatted;
          if (step === 9) this.preClosingCall = formatted;
        }

        if (phase === 'REPORT' || phase === 'ODR_REPORT' || phase === 'SPR_REPORT') {
          if (step === 3) this.sendDraftReportforMR = formatted;
          if (step === 4) this.MRAgreed = formatted;
          if (step === 8) this.revisedTargetPublicationDate = formatted;
          if (step === 9) this.actualPublishingDate = formatted;
        }
        if (phase === 'GL_PRE_PLAN') {
          if (step === 1) this.glPrePlanKickoffDate = this.datePipe.transform(act.DateValue, 'dd-MMM-yyyy') ?? formatted;
          if (step === 2) this.glPrePlanActualKickoffDate = formatted;
        }
        if (phase === 'GL_PRE_CONDUCT') {
          if (step === 1) this.glPreConductFieldworkStartDate = formatted;
        }
        if (phase === 'GL_PRE_REPORT') {
          if (step === 3) this.glPreReportInterimCallDate = formatted;
        }
        if (phase === 'GL_POST_CONDUCT') {
          if (step === 1) this.glPostConductFieldworkStartDate = formatted;
          if (step === 3) this.glPostConductFieldworkEndDate = formatted;
          if (step === 5) this.glPostConductPreClosingCall = formatted;
        }
         if (phase === 'GL_POST_REPORT') {
          if (step === 3) this.glsendDraftReportforMR = formatted;
          if (step === 4) this.glMRAgreed = formatted;
          if (step === 8) this.revisedTargetPublicationDate = formatted;
          if (step === 9) this.glactualPublishingDate = formatted;
        }
      }

      if (act.TextValue && step === 2 && (phase === 'CONDUCT' || phase === 'ODR_CONDUCT' || phase === 'SPR_CONDUCT')) {
        this.step2TextValue = act.TextValue;
      }

      if (step === 3) {
        this.step3ToggleValue = act.ToggleValue;
      }

      if (act.TextValue && step === 5) {
        this.step5TextValue = act.TextValue;
      }

      if (act.TextValue && step === 7) {
        this.reportRating = act.TextValue;
        if(phase === 'GL_POST_REPORT') {
          this.glreportRating = act.TextValue;
        }
      }

      if (step === 2 && (phase === 'REPORT' || phase === 'ODR_REPORT' || phase === 'SPR_REPORT')) {
        if (act.ActivityName === "Review/update Report -1") this.reportReview.reviewedByManager = act.TextValue;
        if (act.ActivityName === "Review/update Report -2") this.reportReview.reviewedByCompliance = act.TextValue;
      }

      if (step === 2 && phase === 'GL_PRE_REPORT') {
        if (act.ActivityName === "Review/Update Interim Pre Go-Live Report -1") this.glPreReportReview.reviewedByManager = act.TextValue;
        if (act.ActivityName === "Review/Update Interim Pre Go-Live Report -2") this.glPreReportReview.reviewedByCompliance = act.TextValue;
      }

      if (step === 2 && phase === 'GL_POST_REPORT') {
        if (act.ActivityName === "Review/Update Interim Post Go-Live Report -1") this.glreportReview.glreviewedByManager = act.TextValue;
        if (act.ActivityName === "Review/Update Interim Post Go-Live Report -2") this.glreportReview.glreviewedByCompliance = act.TextValue;
      }


      if (step === 5 && phase === 'GL_POST_REPORT') {
        this.step5glTextValue = act.TextValue;
        this.glreportRating = act.TextValue;
      }

      // Bind uploaded files (display only)
      if (act.Documents) {
        try {
          const docs = JSON.parse(act.Documents);
          this.uploadedFilesMap[phase][step] = docs.map((d: any) => ({
            name: d.FileName,
            fileName: d.FileName,
            storagePath: d.StorageFilePath,
            existing: true
          }));
        } catch (e) {
          console.error('Document parse error', e);
        }
      }
    });
    this.moveToNextRequiredStep(phase);
    //this.reviewStatusChange.emit(this.getLatestReviewStatus(phase));
  }

  getLatestReviewStatus(phase: PhaseType): string {
    const statusMap: any = {
      PLAN: {
        1: 'PL - Initial Communication Sent',
        3: 'PL - Kick Off Call Held',
        7: 'PL - FW Confirmed',
        8: 'SCP - Engagement Letter Issued',
        9: 'SCP - Engagement Letter Issued'
      },
      CONDUCT: {
        1: 'FW - Started',
        6: 'FW - Completed',
        9: 'RPTG - Pre Close Call Held',
        10: 'RPTG - Pre Close Call Held'
      },
      REPORT: {
        3: 'RPTG - Draft Sent for Mgmt Resp',
        4: 'RPTG - Mgmt Resp Agreed',
        7: 'RPTG - Mgmt Resp Agreed',
        9: 'IM - Published'
      },
  
      GL_PRE_PLAN: {
        2: 'PL - Kick Off Call Held',
        3: 'PL - FW Confirmed',
        4: 'SCP - Engagement Letter Issued'
      },

      GL_PRE_CONDUCT: {
        1: 'Pre-Go-Live FW - Started'
      },

      GL_PRE_REPORT: {
        1: 'Pre-Go-Live FW - Completed',
        3: 'Interim Call Held'
      },

      GL_POST_CONDUCT: {
        1: 'POST-Go-Live FW - Started',
        3: 'POST-Go-Live FW - Completed',
        5: 'RPTG - Pre Close Call Held',
      },

      GL_POST_REPORT: {
        3: 'RPTG - Draft Sent for Mgmt Resp',
        4: 'RPTG - Mgmt Resp Agreed',
        9: 'IM - Published - Issue Management'
      },

      ODR_PLAN: {
        1: 'PL - Initial Communication Sent',
        3: 'PL - Kick Off Call Held',
        7: 'PL - FW Confirmed',
        8: 'SCP - Engagement Letter Issued',
        9: 'SCP - Engagement Letter Issued'
      },

      ODR_CONDUCT: {
        1: 'FW - Started',
        6: 'FW - Completed',
        9: 'RPTG - Pre Close Call Held',
        10: 'RPTG - Pre Close Call Held'
      },

      ODR_REPORT: {
        3: 'RPTG - Draft Sent for Mgmt Resp',
        4: 'RPTG - Mgmt Resp Agreed',
        7: 'RPTG - Mgmt Resp Agreed',
        9: 'IM - Published'
      },

      SPR_PLAN: {
        1: 'PL - Initial Communication Sent',
        3: 'PL - Kick Off Call Held',
        7: 'PL - FW Confirmed',
        8: 'SCP - Engagement Letter Issued',
        9: 'SCP - Engagement Letter Issued'
      },

      SPR_CONDUCT: {
        1: 'FW - Started',
        6: 'FW - Completed',
        9: 'RPTG - Pre Close Call Held',
        10: 'RPTG - Pre Close Call Held'
      },

      SPR_REPORT: {
        3: 'RPTG - Draft Sent for Mgmt Resp',
        4: 'RPTG - Mgmt Resp Agreed',
        7: 'RPTG - Mgmt Resp Agreed',
        9: 'IM - Published'
      },
    };

    const required = PHASE_CONFIG[phase].requiredSteps;
    const completed = this.completedStepsMap[phase];

    const latestStep = [...required]
      .reverse()
      .find(step => completed.includes(step));

    return latestStep ? statusMap[phase][latestStep] : 'Scheduled';
  }

  openUploadDocModal(step: number, phase: PhaseType) {
    // Warning popup will be shown if the step is blocked by sequence rules.
    if (this.isBlockedBySequence(step, phase)) return;
    this.pendingUploadStep = step;
    this.pendingUploadPhase = phase;
    this.showUploadDocModal = true;
    this._lockScroll();
  }

  closeUploadDocModal() {
    this.showUploadDocModal = false;
    this.pendingUploadStep = null;
    this.pendingUploadPhase = null;
    this._unlockScroll();
  }

  onUploadModalFileSelected(event: any) {
    if (this.pendingUploadStep === null || this.pendingUploadPhase === null) return;
    this.onFileSelected(event, this.pendingUploadStep, this.pendingUploadPhase);
    this.showUploadDocModal = false;
    this.pendingUploadStep = null;
    this.pendingUploadPhase = null;
    this._unlockScroll();
  }

  onTextChange(step: number, phase: PhaseType) {
    if (step === 2) {
      this.saveActivity(step, phase, {
        text: this.step2TextValue
      });
    }
    if (step === 5) {
      this.saveActivity(step, phase, {
        text: this.step5TextValue
      });
    }
    if (step === 5 && phase === 'GL_POST_REPORT') {
      this.saveActivity(step, phase, {
        text: this.step5glTextValue
      });
    }
  }

  onToggleChange(step: number, phase: PhaseType) {
    if (step === 3) {
      this.saveActivity(step, phase, {
        toggle: this.step3ToggleValue
      });
    }
  }

  isConductUnlocked(): boolean {
    //return this.isPlanReviewReadOnly || this.completedStepsMap['PLAN']?.includes(9);
    const planPhase = this.getPhaseName('PLAN');
    return this.isPlanReviewReadOnly || this.completedStepsMap[planPhase]?.includes(9);
  }

   isGLConductUnlocked(): boolean {
    return this.isPlanReviewReadOnly || this.completedStepsMap['GL_PRE_PLAN']?.includes(4);
  }

  isReportUnlocked(): boolean {
    //return this.completedStepsMap['CONDUCT']?.includes(10);
    const conductPhase = this.getPhaseName('CONDUCT');  
    return this.completedStepsMap[conductPhase]?.includes(10);
  }

  isGLReportUnlocked(): boolean {
    return this.completedStepsMap['GL_PRE_CONDUCT']?.includes(2);
  }

  isGLPostConductUnlocked(): boolean {
    return this.typeOfReview === 'post-only' || this.completedStepsMap['GL_PRE_REPORT']?.includes(3);
  }

  isGLPostReportUnlocked(): boolean {
    return this.completedStepsMap['GL_POST_CONDUCT']?.includes(6);
  }

  onSubChange(): void {
    const manager = this.reportReview.reviewedByManager;
    const compliance = this.reportReview.reviewedByCompliance;
    const reportPhase = this.getPhaseName('REPORT');
    // reset 2nd only if manager is not selected
    if (!manager) {
      this.reportReview.reviewedByCompliance = '';
      return;
    }

    // API call
    if (manager && compliance) {
      this.saveActivity(2, reportPhase, {
        activityName: 'Review/update Report -1',
        text: manager
      });

      this.saveActivity(2, reportPhase, {
        activityName: 'Review/update Report -2',
        text: compliance
      });
    }
    if (this.reportReview.reviewedByManager && this.reportReview.reviewedByCompliance) {
      this.completeStep(2, reportPhase);
    }
  }

  onSubChangeGLPre(): void {
    const manager = this.glPreReportReview.reviewedByManager;
    const compliance = this.glPreReportReview.reviewedByCompliance;

    if (!manager) {
      this.glPreReportReview.reviewedByCompliance = '';
      return;
    }

    if (manager && compliance) {
      this.saveActivity(2, 'GL_PRE_REPORT', {
        activityName: 'Review/Update Interim Pre Go-Live Report -1',
        text: manager
      });
      this.saveActivity(2, 'GL_PRE_REPORT', {
        activityName: 'Review/Update Interim Pre Go-Live Report -2',
        text: compliance
      });
    }

    if (this.glPreReportReview.reviewedByManager && this.glPreReportReview.reviewedByCompliance) {
      this.completeStep(2, 'GL_PRE_REPORT');
    }
  }

  onSubChangeGLPost(): void {
    const manager = this.glreportReview.glreviewedByManager;
    const compliance = this.glreportReview.glreviewedByCompliance;

    if (!manager) {
      this.glreportReview.glreviewedByCompliance = '';
      return;
    }

    if (manager && compliance) {
      this.saveActivity(2, 'GL_POST_REPORT', {
        activityName: 'Review/Update Interim Post Go-Live Report -1',
        text: manager
      });
      this.saveActivity(2, 'GL_POST_REPORT', {
        activityName: 'Review/Update Interim Post Go-Live Report -2',
        text: compliance
      });
    }

    if (this.glreportReview.glreviewedByManager && this.glreportReview.glreviewedByCompliance) {
      this.completeStep(2, 'GL_POST_REPORT');
    }
  }

  onReportRatingChange(step: number, phase: PhaseType) {
   if (!this.reportRating && !this.glreportRating) {  return; }
    if (phase === 'REPORT' || phase === 'ODR_REPORT' || phase === 'SPR_REPORT') {
    this.saveActivity(step, phase, {
      text: this.reportRating
    }); }
    if (phase === 'GL_POST_REPORT') {
    this.saveActivity(step, phase, {
      text: this.glreportRating
    }); }
  }

  // API for Not Applicable
  async insertNewRequest(phase?: PhaseType, options?: { date?: string, files?: File[], text?: string, afterSave?: () => void }) {
    let requestType, title: any;
    if(phase === 'PLAN' || phase === 'ODR_PLAN' || phase === 'SPR_PLAN' || phase === 'GL_PRE_PLAN') {requestType = "Request Not Applicable Plan Review Activity/Stage"; title = "Request Not Applicable Plan Review Activity/Stage";}
    if(phase === 'REPORT' || phase === 'ODR_REPORT' || phase === 'SPR_REPORT' || phase === 'GL_POST_REPORT') {requestType = "Revised Target Publication Date"; title = "Revised Target Publication Date";}
    this.isLoading = true;
    const params = {
      ReviewID: this.reviewIdNum,
      CreatedBy: this.userName,
      RequestedBy: this.userName,
      category: "InsertNewRequestTesting",
      RequestType: requestType,
      Status: "New",
      Title: title,
      SubTitle: null,
      AssignedTo: (phase === 'PLAN' || phase === 'ODR_PLAN' || phase === 'SPR_PLAN' || phase === 'GL_PRE_PLAN' || phase === 'REPORT' || phase === 'ODR_REPORT' || phase === 'SPR_REPORT' || phase === 'GL_POST_REPORT') ? this.rcManager || null : null,
      EmailEnterpriseID: null,
      FirstName: null,
      LastName: null,
      RCFunctionDesignation: null,
      Country: null,
      WorkLocation: null,
      ExpirationOn: null,
      OperationRequestType: null,
      OperationServiceLine: null,
      ContractNumber: null,
      MasterCustomerNumber: null,
      Market: null,
      MarketUnit: null,
      CancellationReason: null,
      NotApplicableReason: this.notApplicableReason || null,
      RevisedTargetPublicationDate: options?.date || null,
      RevisedDateReason: this.revisedDateReason || null,
      AcknowledgementText: "I acknowledge the Terms & Conditions.",
      AcknowledgementAccepted: true,
      Comments: null,
      Attachment: null,
      DocumentsJSON: '',
    };

    // Attach documents if present
    if (options?.files?.length) {
      const docs = await Promise.all(
        options.files.map(async (f) => {
          const base64 = await this.readFileasBase64(f);

          return {
            DocumentID: null,
            DocumentType: f.name.split('.').pop(),
            FileName: f.name,
            StorageFilePath: `R-1/${f.name}`,
            FileSize: f.size,
            FileType: f.name.split('.').pop(),
            fileContent: base64,
            Description: "Plan Phase Not Applicable Request Supporting Document",
            IsActive: true,
            UploadedBy: this.userName,
            UploadedDate: new Date().toISOString(),
          };
        })
      );
      params.DocumentsJSON = JSON.stringify(docs);
    }
    // API Call
   this.masterService.insertNewRequest(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          console.log('Insert New Request response:', res);
          this.isLoading = false;
          options?.afterSave?.();
        },
        error: err => {
          console.error('insertNewRequest error', err);
          this.isLoading = false;
          this.openWarning('Unable to insert the new request. Please try again.');
        },
      });
  }

  onNotApplicableFileSelected(event: any) {
    const input = event.target;
    const files = input.files;

    if (!files || files.length === 0) return;

    if (this.hasInvalidExtension(files)) {
      input.value = null;
      this.openWarning('Invalid file. Please upload a supported file type (PDF, MSG, XLS, XLSX, DOC, DOCX, TXT, PPT, PPTX, JPG, JPEG, PNG, ZIP) and ensure the file size does not exceed 20 MB.');
      return;
    }

    const emptyFiles = Array.from(files as FileList).filter(f => f.size === 0);
    if (emptyFiles.length > 0) {
      input.value = null;
      this.openWarning('Invalid file. Please upload a supported file type (PDF, MSG, XLS, XLSX, DOC, DOCX, TXT, PPT, PPTX, JPG, JPEG, PNG, ZIP) and ensure the file size does not exceed 20 MB.');
      return;
    }

    const oversized = Array.from(files as FileList).filter(f => f.size > this.maxFileSize);
    if (oversized.length > 0) {
      input.value = null;
      this.openWarning('Invalid file. Please upload a supported file type (PDF, MSG, XLS, XLSX, DOC, DOCX, TXT, PPT, PPTX, JPG, JPEG, PNG, ZIP) and ensure the file size does not exceed 20 MB.');
      return;
    }

    for (let i = 0; i < files.length; i++) {
      this.notApplicableFiles.push(files[i]);
    }
    input.value = null;
  }

  downloadDocument(file: { name: string; fileName: string; storagePath: string; existing?: boolean }): void {
    this.isLoading = true;
    this.reviewerService.downloadIssueDocument(file.storagePath)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob: Blob) => {
          this.isLoading = false;
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = file.fileName || file.storagePath.split('/').pop() || 'download';
          a.click();
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        },
        error: (err: any) => {
          this.isLoading = false;
          console.error('Download error:', err?.status, err?.message);
        },
      });
  }

}